import { PlusCircleIcon } from '@heroicons/react/24/solid';
import React, { useMemo } from 'react';

import { useTheme } from '@/app/ThemeContext';
import { Button } from '@/components/Button';
import { TanStackTable } from '@/components/table/TanStackTable';
import { AllIntegrations } from '@/hooks/useIntegration';
import { SettingContentCard } from '@/sections/settings/components/primitives/SettingContentCard';
import { Account, Integration } from '@/types';

import { getWebhookColumns } from './columns/webhookColumns';

export interface OutgoingWebhookSectionProps {
  /** Filtered webhook notifications only */
  notifications: Account[];
  /** Handler for viewing a webhook */
  onView: (webhook: Account) => void;
  /** Handler for deleting a webhook (hidden when undefined) */
  onDelete?: (webhook: Account) => void;
  /** Handler for adding a new webhook (hidden when undefined) */
  onAdd?: () => void;
  /** Loading status */
  status?: 'error' | 'success' | 'pending';
  /** Error object if status is error */
  error?: Error | null;
}

/**
 * Outgoing Webhook Section
 *
 * Displays configured outgoing webhooks in a TanStackTable with inline copy and Edit/Delete actions.
 * Uses persistent header-in-card pattern matching Email Notifications.
 *
 * Note: This is for OUTGOING webhooks (Integration.webhook) that send
 * notifications to external systems. Not to be confused with incoming
 * webhooks (Integration.hook) used for data ingestion via InboundWebhookSection.
 *
 * Structure:
 * - Header Row: Always visible with icon, title, description, and "Add Webhook" button
 * - Content Row: TanStackTable shown only when webhooks exist (conditional)
 *
 * Used in:
 * - NotificationSettingsCards
 */
export const OutgoingWebhookSection: React.FC<OutgoingWebhookSectionProps> = ({
  notifications,
  onView,
  onDelete,
  onAdd,
  status = 'success',
  error = null,
}) => {
  const { currentTheme } = useTheme();
  const webhookMeta = AllIntegrations[Integration.webhook];

  // Column definitions with action handlers
  const columns = useMemo(
    () =>
      getWebhookColumns({
        onView,
        onDelete,
      }),
    [onView, onDelete]
  );

  return (
    <section aria-labelledby="webhooks-heading" data-testid="webhooks-section">
      <SettingContentCard padding="sm">
        <div className="flex flex-col gap-4">
          {/* Header Row - ALWAYS visible */}
          <div>
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <img
                  src={`/icons/${currentTheme}/${webhookMeta.logo}`}
                  alt={webhookMeta.name}
                  className="size-5 shrink-0 opacity-50"
                />
                <div className="flex flex-col gap-1">
                  <h4 id="webhooks-heading" className="font-bold">
                    Outbound Webhooks
                  </h4>
                  <span className="text-chariot-text-secondary text-xs">
                    Send security notifications to external systems via webhooks
                  </span>
                </div>
              </div>
              {onAdd && (
                <Button
                  onClick={onAdd}
                  label="Add Webhook"
                  styleType="primary"
                  size="xs"
                  startIcon={<PlusCircleIcon className="size-4" />}
                  data-testid="webhooks-add-button"
                />
              )}
            </div>
          </div>

          {/* Table Row - Only when data exists */}
          {notifications.length > 0 && (
            <div className="border-chariot-stroke overflow-hidden rounded-lg border">
              <TanStackTable
                name="webhooks"
                columns={columns}
                data={notifications}
                status={status}
                error={error}
              />
            </div>
          )}
        </div>
      </SettingContentCard>
    </section>
  );
};
